import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { surveysAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    FileText,
    Plus,
    Users,
    Calendar,
    ChevronRight,
    Trash2,
    CheckCircle
} from 'lucide-react';
import { formatDate } from '../lib/utils';

const SurveyCard = ({ survey, onTakeSurvey, isHR }) => (
    <Card className="bento-card-interactive">
        <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-stone-900 truncate">{survey.title}</h4>
                    <p className="text-sm text-stone-500 line-clamp-2 mt-1">{survey.description}</p>
                </div>
                <Badge variant="outline" className={survey.status === 'active' ? 'bg-emerald-50 text-emerald-700' : ''}>
                    {survey.status}
                </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-stone-500">
                <div className="flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    {survey.questions?.length || 0} questions
                </div>
                <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {survey.responses_count || 0} responses
                </div>
                <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(survey.created_at)}
                </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
                {survey.is_anonymous && (
                    <Badge variant="outline" className="text-xs">Anonymous</Badge>
                )}
                {!isHR && survey.status === 'active' && (
                    <Button 
                        size="sm" 
                        className="ml-auto"
                        onClick={() => onTakeSurvey(survey)}
                        data-testid={`take-survey-${survey.id}`}
                    >
                        Take Survey
                        <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                )}
            </div>
        </CardContent>
    </Card>
);

export default function SurveysPage() {
    const { isHR } = useAuth();
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    
    // Survey response state
    const [selectedSurvey, setSelectedSurvey] = useState(null);
    const [responses, setResponses] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Form state
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [isAnonymous, setIsAnonymous] = useState(true);
    const [questions, setQuestions] = useState([{ text: '', type: 'text' }]);

    useEffect(() => {
        fetchSurveys();
    }, []);

    const fetchSurveys = async () => {
        try {
            setLoading(true);
            const response = await surveysAPI.getAll();
            setSurveys(response.data || []);
        } catch (error) {
            console.error('Failed to fetch surveys:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddQuestion = () => {
        setQuestions([...questions, { text: '', type: 'text' }]);
    };

    const handleRemoveQuestion = (index) => {
        setQuestions(questions.filter((_, i) => i !== index));
    };

    const handleQuestionChange = (index, value) => {
        const updated = [...questions];
        updated[index].text = value;
        setQuestions(updated);
    };

    const handleCreate = async () => {
        if (!title.trim() || questions.some(q => !q.text.trim())) return;

        try {
            setCreating(true);
            await surveysAPI.create({
                title,
                description,
                is_anonymous: isAnonymous,
                questions: questions.map((q, i) => ({ id: i + 1, ...q }))
            });
            setDialogOpen(false);
            setTitle('');
            setDescription('');
            setQuestions([{ text: '', type: 'text' }]);
            await fetchSurveys();
        } catch (error) {
            console.error('Failed to create survey:', error);
        } finally {
            setCreating(false);
        }
    };

    const handleTakeSurvey = (survey) => {
        setSelectedSurvey(survey);
        setResponses({});
        setSubmitted(false);
    };

    const handleResponseChange = (questionId, value) => {
        setResponses(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    const handleSubmitSurvey = async () => {
        if (!selectedSurvey) return;
        
        try {
            setSubmitting(true);
            const responseList = Object.entries(responses).map(([questionId, answer]) => ({
                question_id: parseInt(questionId),
                answer: answer
            }));
            await surveysAPI.respond(selectedSurvey.id, { responses: responseList });
            setSubmitted(true);
            await fetchSurveys();
        } catch (error) {
            console.error('Failed to submit survey:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const closeSurveyDialog = () => {
        setSelectedSurvey(null);
        setResponses({});
        setSubmitted(false);
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500">Loading surveys...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8" data-testid="surveys-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold font-heading text-stone-900 flex items-center gap-3">
                            <FileText className="w-8 h-8 text-indigo-600" />
                            Surveys
                        </h1>
                        <p className="text-stone-500 mt-1">
                            {isHR() ? 'Create and manage employee surveys.' : 'Complete available surveys.'}
                        </p>
                    </div>
                    {isHR() && (
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                            <DialogTrigger asChild>
                                <Button className="gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="create-survey-btn">
                                    <Plus className="w-4 h-4" />
                                    Create Survey
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle>Create New Survey</DialogTitle>
                                    <DialogDescription>
                                        Create a survey to collect feedback from employees.
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 mt-4">
                                    <div>
                                        <Label htmlFor="title">Survey Title</Label>
                                        <Input
                                            id="title"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            placeholder="e.g., Q4 Engagement Survey"
                                            className="mt-1"
                                            data-testid="survey-title-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="description">Description</Label>
                                        <Textarea
                                            id="description"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Brief description of the survey purpose..."
                                            className="mt-1"
                                            data-testid="survey-description-input"
                                        />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="anonymous">Anonymous Responses</Label>
                                        <Switch
                                            id="anonymous"
                                            checked={isAnonymous}
                                            onCheckedChange={setIsAnonymous}
                                        />
                                    </div>
                                    <div>
                                        <Label>Questions</Label>
                                        <div className="space-y-2 mt-2">
                                            {questions.map((q, index) => (
                                                <div key={index} className="flex gap-2">
                                                    <Input
                                                        value={q.text}
                                                        onChange={(e) => handleQuestionChange(index, e.target.value)}
                                                        placeholder={`Question ${index + 1}`}
                                                        data-testid={`question-input-${index}`}
                                                    />
                                                    {questions.length > 1 && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleRemoveQuestion(index)}
                                                        >
                                                            <Trash2 className="w-4 h-4 text-red-500" />
                                                        </Button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleAddQuestion}
                                            className="mt-2"
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            Add Question
                                        </Button>
                                    </div>
                                    <Button
                                        onClick={handleCreate}
                                        disabled={creating || !title.trim()}
                                        className="w-full bg-indigo-600 hover:bg-indigo-700"
                                        data-testid="submit-survey-btn"
                                    >
                                        {creating ? 'Creating...' : 'Create Survey'}
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>

                {/* Surveys Grid */}
                {surveys.length === 0 ? (
                    <Card className="bento-card">
                        <CardContent className="p-12 text-center">
                            <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4">
                                <FileText className="w-8 h-8 text-stone-400" />
                            </div>
                            <h3 className="text-lg font-medium text-stone-900 mb-2">No surveys yet</h3>
                            <p className="text-stone-500">
                                {isHR() ? 'Create your first survey to start collecting feedback.' : 'No surveys available at the moment.'}
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {surveys.map((survey) => (
                            <SurveyCard 
                                key={survey.id} 
                                survey={survey} 
                                onTakeSurvey={handleTakeSurvey}
                                isHR={isHR()}
                            />
                        ))}
                    </div>
                )}
            </motion.div>

            {/* Take Survey Dialog */}
            <Dialog open={!!selectedSurvey} onOpenChange={closeSurveyDialog}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{selectedSurvey?.title}</DialogTitle>
                        <DialogDescription>{selectedSurvey?.description}</DialogDescription>
                    </DialogHeader>
                    
                    {submitted ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-emerald-600" />
                            </div>
                            <h3 className="text-lg font-medium text-stone-900 mb-2">Thank you!</h3>
                            <p className="text-stone-500 mb-4">Your response has been submitted successfully.</p>
                            <Button onClick={closeSurveyDialog}>Close</Button>
                        </div>
                    ) : (
                        <div className="space-y-6 mt-4">
                            {selectedSurvey?.questions?.map((question, index) => (
                                <div key={question.id || index} className="space-y-2">
                                    <Label className="text-base font-medium">
                                        {index + 1}. {question.text}
                                    </Label>
                                    {question.type === 'rating' ? (
                                        <div className="pt-2">
                                            <Slider
                                                value={[responses[question.id] || 3]}
                                                onValueChange={(val) => handleResponseChange(question.id, val[0])}
                                                max={5}
                                                min={1}
                                                step={1}
                                                className="w-full"
                                            />
                                            <div className="flex justify-between text-xs text-stone-500 mt-1">
                                                <span>1 (Low)</span>
                                                <span>{responses[question.id] || 3}</span>
                                                <span>5 (High)</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <Textarea
                                            placeholder="Type your answer here..."
                                            value={responses[question.id] || ''}
                                            onChange={(e) => handleResponseChange(question.id, e.target.value)}
                                            className="min-h-[80px]"
                                        />
                                    )}
                                </div>
                            ))}
                            
                            <div className="flex gap-3 pt-4 border-t">
                                <Button 
                                    variant="outline" 
                                    onClick={closeSurveyDialog}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    onClick={handleSubmitSurvey}
                                    disabled={submitting}
                                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                                    data-testid="submit-response-btn"
                                >
                                    {submitting ? 'Submitting...' : 'Submit Response'}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
